import React, { useEffect, useRef, useState } from "react";
import { FormHandler } from "./form-handler";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import useDynamicForm from "./useDynamicForm";
import UIList, { UIListItem } from "../list";
import { getLoggedinUser } from "@/store/slices/auth/authSlice";
import { useSelector } from "react-redux";
import { knowncolumns } from "../grid-wrapper/dynamic-columns-generator";
import FormFieldsWrapper from "./FormFieldsWrapper";
import AddCustomField from "./AddCustomField";
import useRoles from "@/hooks/useRoles";
import * as styles from "./styles.module.scss";
import { InfoOutlined } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import { RemoveDuplicates } from "@/services/generic.methods";

const staticWidth = 380;
const DynamicForm = (props) => {
  const {
    formJson,
    apiData,
    handleSubmit,
    formId = "form_1",
    fluidView,
    saveOnChange,
    quickUpdate = false,
    disableSubmit,
    accordins = false,
    editMode = true,
    reset,
    sectionStyles = {},
    setFormIkErrors = () => {},
    onValuesChange,
    draftValues = null,
    onSectionChange,
    initialSectionId,
    requiredFieldNames,
    serverValidation = true,
  } = props;
  const { sections } = formJson;

  const [invalidFields, setInvalidFields] = useState({});
  const draftSyncDoneRef = useRef(false);
  const [validatingFields, setValidatingFields] = useState({});
  const user = useSelector(getLoggedinUser);
  const permission = useRoles("customField").getPermission;

  const handleValidatingChange = (fieldName, isValidating) => {
    setValidatingFields((prev) => {
      const next = { ...prev };
      if (isValidating) {
        next[fieldName] = true;
      } else {
        delete next[fieldName];
      }
      return next;
    });
  };

  const handleValidate = () => {
    const errors = {};
    const combinedErrors = {
      ...errors,
      ...invalidFields,
    };
    return combinedErrors;
  };

  const FormikForm = FormHandler({ sections, handleSubmit, handleValidate });
  const { validateServerSide, onFormSubmit } = useDynamicForm({
    FormikForm,
    invalidFields,
    setInvalidFields,
    apiData,
    formJson,
    requiredFieldNames,
    draftValues,
    validatingFields,
  });

  // Appointment inline-draft flow: validate on submit, not by disabling Save.
  const validateOnSubmitOnly = (requiredFieldNames?.length ?? 0) > 0;

  const syncDisableSubmit = () => {
    if (!disableSubmit) return;
    setTimeout(() => {
      if (validateOnSubmitOnly) {
        disableSubmit(Object.keys(invalidFields).length > 0);
        return;
      }
      FormikForm.validateForm().then((schemaErrors) => {
        const allErrors = { ...schemaErrors, ...invalidFields };
        FormikForm.setErrors(allErrors);
        disableSubmit(Object.keys(allErrors).length > 0);
      });
    }, 0);
  };

  const [serverData, setServerData] = useState({});
  const [fieldProps, setFieldProps] = useState({});
  const [sectionId, setSectionId] = useState(
    initialSectionId !== undefined ? initialSectionId : sections?.[0]?.id,
  );

  useEffect(() => {
    setInitialFormData();
  }, [apiData]);

  // Re-apply draft when draftValues arrives after mount (e.g. modal reopen after
  // lead-create cancel). Skip while the user is editing to avoid reset on each keystroke.
  useEffect(() => {
    if (!draftValues || Object.keys(draftValues).length === 0) return;
    if (FormikForm.dirty) return;

    const clonedDraft = JSON.parse(JSON.stringify(draftValues));

    if (apiData) {
      const savedData = {
        ...FormikForm.values,
        ...clonedDraft,
      };
      savedData.leadTags = RemoveDuplicates(serverData?.leadTags);
      FormikForm.setValues(savedData);
      setServerData(savedData);
      setFieldProps((prev) => ({ ...prev, serverData: savedData }));
    } else {
      const defaults = {
        ...FormikForm.values,
        leadStatusId: knowncolumns.LeadStatusId().find((val) => val.isDefault)
          ?.value,
        leadOwnerId: user?.UserID,
        countryCode: "+1",
        countryISOCode: "US",
        leadTypeId: "",
      };
      let merged = { ...defaults, ...clonedDraft };
      merged.leadTags = RemoveDuplicates(merged?.leadTags);
      FormikForm.resetForm({ values: merged });
      setServerData(merged);
      setFieldProps((prev) => ({ ...prev, serverData: merged }));
    }

    const clearedErrors = { ...FormikForm.errors };
    Object.keys(clonedDraft).forEach((key) => {
      const val = clonedDraft[key];
      if (val != null && String(val).trim() !== "") {
        delete clearedErrors[key];
      }
    });
    if (
      Object.keys(clearedErrors).length !==
      Object.keys(FormikForm.errors || {}).length
    ) {
      FormikForm.setErrors(clearedErrors);
    }
    draftSyncDoneRef.current = false;
  }, [draftValues]);

  // After draft values are applied to Formik, re-validate and sync Save state.
  // resetForm clears dirty, so we cannot wait on FormikForm.dirty.
  useEffect(() => {
    if (!draftValues || Object.keys(draftValues).length === 0) return;
    if (draftSyncDoneRef.current) return;
    const draftApplied = Object.keys(draftValues).some((key) => {
      const draftVal = draftValues[key];
      if (draftVal == null || String(draftVal).trim() === "") return false;
      return (
        String(FormikForm.values?.[key] ?? "").trim() ===
        String(draftVal).trim()
      );
    });
    if (!draftApplied) return;
    draftSyncDoneRef.current = true;
    syncDisableSubmit();
  }, [FormikForm.values, draftValues]);

  useEffect(() => {
    if (reset) {
      setInitialFormData();
      setTimeout(() => {
        FormikForm.setErrors({});
        setFormIkErrors({});
      });
    }
  }, [reset]);

  useEffect(() => {
    if (!disableSubmit) return;
    if (validateOnSubmitOnly) {
      disableSubmit(Object.keys(invalidFields).length > 0);
      return;
    }
    const allErrors = { ...(FormikForm?.errors ?? {}), ...invalidFields };
    const hasErrors = Object.keys(FormikForm?.errors || {}).length > 0;
    const isChecking = Object.keys(validatingFields).length > 0;
    disableSubmit(Object.keys(allErrors).length > 0 || hasErrors || isChecking);
  }, [FormikForm?.errors, validatingFields, disableSubmit, invalidFields, validateOnSubmitOnly]);

  const setInitialFormData = () => {
    if (apiData) {
      let salutation = apiData?.salutation;
      if (salutation) {
        salutation = salutation?.replace(".", "");
      }
      // Start from Formik's own initial values so every form field key is
      // always present (e.g. stateProvinceId). Then overlay API data, then
      // any unsaved draft. Using JSON round-trip to ensure we always work
      // with a plain, unfrozen object — never a frozen Immer Redux proxy.
      let savedData = {
        ...FormikForm.values,
        ...apiData,
        salutation,
        countryCode: apiData.countryCode || "+1",
        countryISOCode: apiData?.countryISOCode || "US",
      };
      const hasDraft = draftValues && Object.keys(draftValues).length > 0;
      if (hasDraft) {
        // Shallow-clone draftValues to guarantee it is a plain, unfrozen object
        savedData = {
          ...savedData,
          ...JSON.parse(JSON.stringify(draftValues)),
        };
      }
      FormikForm.setValues(savedData);
      setServerData(savedData);
      if (hasDraft) {
        draftSyncDoneRef.current = false;
      }
      setFieldProps({ ...fieldProps, serverData: savedData, onValidatingChange: handleValidatingChange });
    } else {
      const defaults = {
        ...FormikForm.values,
        leadStatusId: knowncolumns.LeadStatusId().find((val) => val.isDefault)
          ?.value,
        leadOwnerId: user?.UserID,
        countryCode: "+1",
        countryISOCode: "US",
        leadTypeId: "", // for default none selected
      };
      // Restore draft values on top of defaults for create mode
      const hasDraftInElse = draftValues && Object.keys(draftValues).length > 0;
      const merged = hasDraftInElse
        ? { ...defaults, ...draftValues }
        : defaults;
      FormikForm.resetForm({ values: merged });
      setServerData(merged);
      setFieldProps({ ...fieldProps, serverData: merged });

      if (hasDraftInElse) {
        draftSyncDoneRef.current = false;
      }
    }
  };

  // Notify parent of value changes - only when user actually modifies the form
  useEffect(() => {
    if (!onValuesChange) return;
    if (!FormikForm?.dirty) return;
    onValuesChange(FormikForm.values);
  }, [FormikForm.values, FormikForm.dirty]);

  const handleClick = (item) => {
    const next = item.id === sectionId ? "" : item?.id;
    setSectionId(next);
    if (onSectionChange) onSectionChange(next);
  };

  useEffect(() => {
    setFormIkErrors(FormikForm.errors);
  }, [FormikForm.errors]);

  useEffect(() => {
    setFieldProps({
      disableSubmit,
      validateServerSide,
      saveOnChange,
      quickUpdate,
      serverData,
      setServerData,
      serverValidation,
      onValidatingChange: handleValidatingChange,
      invalidFields,
    });
  }, []);

  useEffect(() => {
    setFieldProps({
      disableSubmit,
      validateServerSide,
      saveOnChange,
      quickUpdate,
      serverData,
      setServerData,
      serverValidation,
      onValidatingChange: handleValidatingChange,
      invalidFields,
    });
  }, [serverData, serverValidation, invalidFields]);

  const accordionHeaderRight = (section) => {
    if (section?.showAdd && permission?.edit) {
      return <AddCustomField {...section} />;
    }
    return null;
  };

  return (
    <form id={formId} onSubmit={onFormSubmit} autoComplete="off" noValidate>
      {accordins ? (
        <UIList sx={{ pt: 0 }}>
          {sections && sections.length
            ? sections.map((section, index) => (
                <UIListItem
                  key={index}
                  title={section.heading}
                  accordionHeaderRight={accordionHeaderRight(section)}
                  expanded={sectionId}
                  id={section?.id}
                  handleItemClick={() => handleClick(section)}
                  parentSelected={sectionId}
                  headingStyle={{ flex: "none" }}
                  sx={{
                    color: "var(--palette-sub_heading) !important",
                    "&:hover": {
                      backgroundColor: "transparent !important",
                      color: "var(--palette-secondary_button_text) !important",
                      // backgroundColor: "var(--palette-hover_color) !important",
                    },
                    "&.Mui-selected, &.Mui-selected:hover": {
                      // backgroundColor: "white !important",
                      backgroundColor: "transparent !important",
                      color: "var(--palette-sub_heading) !important",
                    },
                    ...(index !== 0
                      ? {
                          borderTop: "1px solid var(--palette-stroke)",
                        }
                      : {}),
                  }}
                >
                  <div key={index} style={{ margin: "1rem" }}>
                    <SectionFields
                      section={section}
                      fluidView={fluidView}
                      FormikForm={FormikForm}
                      fieldProps={fieldProps}
                      editMode={editMode}
                      sectionStyles={sectionStyles}
                      setInvalidFields={setInvalidFields}
                    />
                  </div>
                </UIListItem>
              ))
            : null}
        </UIList>
      ) : sections && sections.length ? (
        sections.map((section, index) => (
          <div key={index} style={index > 0 && !section?.heading ? { marginTop: "25px" } : {}}>
            {section?.heading && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mt: 2.5,
                  mb: 1,
                  p: "10px 5px",
                }}
              >
                <Typography variant="h5">{section.heading}</Typography>
                {section?.infoText ? (
                  <Tooltip
                    title={section?.infoText}
                    placement="top-start"
                    PopperProps={{
                      sx: {
                        "& .MuiTooltip-tooltip": {
                          maxWidth: 170,
                        },
                      },
                    }}
                  >
                    <InfoOutlined
                      sx={{
                        fontSize: 16,
                        verticalAlign: "top",
                        color: "var(--palette-text)",
                      }}
                    />
                  </Tooltip>
                ) : null}
              </Box>
            )}
            <SectionFields
              section={section}
              fluidView={fluidView}
              FormikForm={FormikForm}
              fieldProps={fieldProps}
              editMode={editMode}
              sectionStyles={sectionStyles}
              setInvalidFields={setInvalidFields}
            />
          </div>
        ))
      ) : null}
    </form>
  );
};

export const SectionFields = ({
  section,
  fluidView,
  FormikForm,
  fieldProps,
  editMode = true,
  sectionStyles,
  setInvalidFields,
  columnWidth,
  lastFieldFullWidth = false,
}) => {
  const filterFields = (field, serverData) => {
    if (field.fieldHiddenTemp) {
      return serverData?.[field.name]?.length > 0;
    }
    return !field.fieldHidden;
  };
  return (
    <div
      id={"dyna-form-1"}
      className={lastFieldFullWidth ? styles.lastFieldFullWidth : ""}
      style={{
        display: "grid",
        width: "100%",
        minWidth: 0,
        gridTemplateColumns: fluidView
          ? "1fr"
          : `repeat(auto-fit, minmax(min(100%, ${columnWidth ?? staticWidth}px), 1fr))`,
        gap: "25px",
        ...sectionStyles,
      }}
    >
      {section.fields && section.fields.length
        ? section.fields
            .filter((field) => filterFields(field, fieldProps.serverData))
            .map((item, innerIndex) => (
              <Box
                key={innerIndex}
                sx={{
                  minWidth: 0,
                  maxWidth: "100%",
                  ...(item.type == "text-area" && !fluidView
                    ? { gridColumn: "1 / -1" }
                    : {}),
                }}
                className={`${item?.className ?? ""} ${
                  // item?.isFirstClearedField ? "cleared-field" :
                  ""
                }`}
              >
                <FormFieldsWrapper
                  {...{
                    ...item,
                    FormikForm,
                    ...fieldProps,
                    ["disableFields"]: !editMode,
                    setInvalidFields,
                  }}
                />
              </Box>
            ))
        : null}
    </div>
  );
};

export default DynamicForm;
